import { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import styled from '@emotion/styled';
import {
  DataGrid,
  GridColDef,
  GridToolbar,
  GridRowSelectionModel,
  useGridApiRef,
  gridFilteredSortedRowEntriesSelector,
} from '@mui/x-data-grid';
import Popover from '@mui/material/Popover';
import CircularProgress from '@mui/material/CircularProgress';
import type { MarketingMessage, MessageGenPhase, MessageMetrics } from '@trinity/chat';
import { Tooltip } from '@trinity/chat-components/components/presentation/Tooltip.js';
import { FileLayoutSelector } from '@trinity/chat-components/components/presentation/FileLayoutSelector/index.js';
import { SupportingEvidenceModal } from './SupportingEvidenceModal.js';

type ViewMode = 'table' | 'cards';

const METRIC_KEYS = [
  'clarity',
  'motivating',
  'credibility',
  'differentiation',
  'objectiveResonance',
  'brandAlignment',
  'audienceFit',
  'relevance',
] as const satisfies readonly (keyof MessageMetrics)[];

const CARDS_PER_PAGE = 12;

function camelToTitle(key: string): string {
  return key.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase());
}

function normalizeCategory(value: string): string {
  return value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function titleCase(value: string): string {
  return value.replace(/\b\w/g, (c) => c.toUpperCase());
}

type ConfidenceLevel = 'low' | 'medium' | 'high';

function getConfidenceLevel(value: string): ConfidenceLevel {
  const lower = value.toLowerCase();
  if (lower.includes('high')) return 'high';
  if (lower.includes('medium')) return 'medium';
  return 'low';
}

function formatConfidence(value: string): string {
  const titled = titleCase(value);
  if (titled.toLowerCase().includes('confidence')) return titled;
  return `${titled} Confidence`;
}

export interface MessageGenerationTableProps {
  messages: MarketingMessage[];
  isGenerating: boolean;
  sessionId?: number;
  numberOfMessages?: number;
  phase?: MessageGenPhase;
  selectionModel: GridRowSelectionModel;
  onSelectionModelChange: (newSelection: GridRowSelectionModel) => void;
  onSaveSelected?: (messageIdentifiers: string[]) => void;
  isSaving?: boolean;
}

/**
 * Check if messages can be saved based on the current phase.
 * Messages can only be saved after curation is complete.
 */
function canSaveMessages(phase?: MessageGenPhase): boolean {
  return phase === 'curated';
}

export function MessageGenerationTable({
  messages,
  isGenerating,
  sessionId,
  numberOfMessages = 5,
  phase,
  selectionModel,
  onSelectionModelChange,
  onSaveSelected,
  isSaving = false,
}: MessageGenerationTableProps): React.JSX.Element {
  const savingEnabled = canSaveMessages(phase);

  const [viewMode, setViewMode] = useState<ViewMode>('cards');
  const apiRef = useGridApiRef();
  const [cardRows, setCardRows] = useState<MarketingMessage[]>([]);
  const [cardPage, setCardPage] = useState(0);
  const [evidenceMessage, setEvidenceMessage] = useState<MarketingMessage | null>(null);

  const syncCardRows = useCallback(() => {
    if (!apiRef.current?.state) {
      setCardRows(messages);
      return;
    }
    try {
      const entries = gridFilteredSortedRowEntriesSelector(apiRef);
      setCardRows(entries.map((e) => e.model as MarketingMessage));
    } catch {
      setCardRows(messages);
    }
  }, [apiRef, messages]);

  // Sync card rows when switching to card view or when messages change
  useEffect(() => {
    if (viewMode === 'cards') {
      const timer = setTimeout(syncCardRows, 0);
      return () => clearTimeout(timer);
    }
  }, [viewMode, messages, syncCardRows]);

  // Sync card rows whenever the DataGrid's internal state changes (filter/sort applied)
  const syncTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const handleGridStateChange = useCallback(() => {
    if (viewMode !== 'cards') return;
    clearTimeout(syncTimerRef.current);
    syncTimerRef.current = setTimeout(syncCardRows, 0);
  }, [viewMode, syncCardRows]);

  const hasRank = useMemo(() => messages.some((m) => m.seqIdx != null), [messages]);

  const totalCardPages = Math.ceil(cardRows.length / CARDS_PER_PAGE);

  // Auto-adjust page if out of bounds
  const validCardPage = Math.min(cardPage, Math.max(0, totalCardPages - 1));

  const columns: GridColDef[] = useMemo(() => {
    const cols: GridColDef[] = [];

    // Add checkbox column for selection
    if (savingEnabled) {
      cols.push({
        field: 'select',
        headerName: '',
        width: 50,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => {
          const row = params.row as MarketingMessage;
          const isSelected = (selectionModel as string[]).includes(row.identifier);

          if (row.saved) {
            return (
              <TableSavedCheckmark title="Already saved">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </TableSavedCheckmark>
            );
          }

          return (
            <TableCheckbox
              type="checkbox"
              checked={isSelected}
              onChange={(e) => {
                e.stopPropagation();
                const currentIds = selectionModel as string[];
                if (isSelected) {
                  onSelectionModelChange(currentIds.filter((id) => id !== row.identifier));
                } else {
                  onSelectionModelChange([...currentIds, row.identifier]);
                }
              }}
              onClick={(e) => e.stopPropagation()}
            />
          );
        },
      });
    }

    if (hasRank) {
      cols.push({
        field: 'seqIdx',
        headerName: 'Rank',
        width: 70,
        type: 'number',
      });
    }

    cols.push(
      {
        field: 'headline',
        headerName: 'Title',
        width: 250,
        flex: 1.5,
        renderCell: (params) => <TitleCell>{params.value}</TitleCell>,
      },
      {
        field: 'body',
        headerName: 'Message',
        width: 400,
        flex: 3,
        renderCell: (params) => <MessageCell>{params.value}</MessageCell>,
      },
      {
        field: 'category',
        headerName: 'Category',
        width: 110,
        renderCell: (params) => <CategoryBadge>{normalizeCategory(params.value as string)}</CategoryBadge>,
      },
      {
        field: 'confidence',
        headerName: 'Confidence',
        width: 100,
        renderCell: (params) => {
          const row = params.row as MarketingMessage;
          const value = row.confidence;
          if (!value) return null;
          const display = titleCase(value);
          const level = getConfidenceLevel(value);
          const basis = row.confidenceBasis;
          return basis ? (
            <Tooltip title={basis} enterDelay={500}>
              <ConfidenceBadge $level={level}>{display}</ConfidenceBadge>
            </Tooltip>
          ) : (
            <ConfidenceBadge $level={level}>{display}</ConfidenceBadge>
          );
        },
      },
      {
        field: 'doctorNeedAddressed',
        headerName: 'HCP Matched Attributes',
        width: 250,
        valueGetter: (_value: unknown, row: MarketingMessage) => {
          const d = row.doctorNeedAddressed;
          if (!d) return '';
          return `${d.themeLabel} / ${d.domainLabel} / ${d.frame}`;
        },
        renderCell: (params) => {
          const text = params.value as string;
          if (!text) return null;
          return (
            <Tooltip title={text} enterDelay={500}>
              <TooltipCell>{text}</TooltipCell>
            </Tooltip>
          );
        },
      },
      {
        field: 'metrics',
        headerName: 'Metrics',
        width: 90,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const row = params.row as MarketingMessage;
          if (!row.metrics) return null;
          return <MetricsPopoverTrigger metrics={row.metrics} />;
        },
      },
      {
        field: 'evidence',
        headerName: 'Evidence',
        width: 90,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const row = params.row as MarketingMessage;
          return (
            <EvidenceIconButton onClick={() => setEvidenceMessage(row)} title="View Evidence">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85zm-5.242.156a5 5 0 1 1 0-10 5 5 0 0 1 0 10z" />
              </svg>
            </EvidenceIconButton>
          );
        },
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 130,
        renderCell: (params) => (
          <StatusBadge status={params.value as 'draft' | 'approved' | 'published'}>
            {params.value}
          </StatusBadge>
        ),
      }
    );

    return cols;
  }, [hasRank, savingEnabled, selectionModel, onSelectionModelChange]);

  const rows = useMemo(() => messages, [messages]);

  // Generate skeleton rows while loading
  const skeletonRows = useMemo(
    () =>
      Array.from({ length: Math.min(numberOfMessages, 10) }).map((_, index) => ({
        identifier: `skeleton-${index}`,
        seqIdx: null,
        headline: '',
        body: '',
        category: '',
        status: 'draft' as const,
        confidence: null,
        confidenceBasis: null,
        doctorNeedAddressed: null,
        metrics: null,
      })),
    [numberOfMessages]
  );

  const pagedCardRows = useMemo(() => {
    const start = validCardPage * CARDS_PER_PAGE;
    return cardRows.slice(start, start + CARDS_PER_PAGE);
  }, [cardRows, validCardPage]);

  const handleCardSelect = useCallback(
    (identifier: string) => {
      if (!savingEnabled) return;
      const msg = messages.find((m) => m.identifier === identifier);
      if (!msg || msg.saved) return;
      const isSelected = (selectionModel as string[]).includes(identifier);
      if (isSelected) {
        onSelectionModelChange((selectionModel as string[]).filter((id) => id !== identifier));
      } else {
        onSelectionModelChange([...(selectionModel as string[]), identifier]);
      }
    },
    [savingEnabled, messages, selectionModel, onSelectionModelChange]
  );

  const isCardView = viewMode === 'cards';

  // Calculate selected unsaved messages
  const selectedUnsavedIds = useMemo(() => {
    const selectedIds = selectionModel as string[];
    return selectedIds.filter((id) => {
      const msg = messages.find((m) => m.identifier === id);
      return msg && !msg.saved;
    });
  }, [selectionModel, messages]);

  const hasSelection = selectedUnsavedIds.length > 0;

  const handleSaveClick = useCallback(() => {
    if (onSaveSelected && selectedUnsavedIds.length > 0) {
      onSaveSelected(selectedUnsavedIds);
    }
  }, [onSaveSelected, selectedUnsavedIds]);

  const handleClearSelection = useCallback(() => {
    onSelectionModelChange([]);
  }, [onSelectionModelChange]);

  return (
    <Root>
      <ToggleRow>
        <FileLayoutSelector
          selected={viewMode === 'cards' ? 'grid' : 'list'}
          onSelect={(layout) => setViewMode(layout === 'grid' ? 'cards' : 'table')}
        />
      </ToggleRow>
      <DataGridContainer $cardMode={isCardView}>
        <DataGrid
          apiRef={apiRef}
          rows={isGenerating ? skeletonRows : rows}
          columns={columns}
          getRowId={(row) => row.identifier}
          loading={isGenerating}
          checkboxSelection={false}
          disableRowSelectionOnClick
          onStateChange={handleGridStateChange}
          getRowClassName={(params) => (params.row.saved ? 'saved-row' : '')}
          density="compact"
          slots={{
            toolbar: GridToolbar,
          }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
              csvOptions: { fileName: `messages-session-${sessionId}` },
            },
          }}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 25 },
            },
            sorting: hasRank ? { sortModel: [{ field: 'seqIdx', sort: 'asc' }] } : undefined,
          }}
          pageSizeOptions={[10, 25, 50, 100]}
          sx={{
            border: 'none',
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#f8f9fa',
              borderBottom: '2px solid #e0e0e0',
              fontSize: '10px',
              fontWeight: 600,
              color: '#1a1a1a',
              textTransform: 'uppercase',
              letterSpacing: '0.3px',
              minHeight: '36px !important',
              maxHeight: '36px !important',
            },
            '& .MuiDataGrid-columnHeader': {
              padding: '0 8px',
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              fontSize: '10px',
              fontWeight: 600,
            },
            '& .MuiDataGrid-row': {
              '&.saved-row': {
                backgroundColor: 'rgba(16, 185, 129, 0.1) !important',
              },
              '&.Mui-selected': {
                backgroundColor: 'rgba(18, 113, 196, 0.08) !important',
              },
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: '#f9f9f9',
            },
            '& .MuiDataGrid-cell': {
              fontSize: '12px',
              color: '#424242',
              borderBottom: '1px solid #e8e8e8',
              padding: '6px 8px',
            },
            '& .MuiCheckbox-root.Mui-disabled': {
              color: '#d1d5db',
            },
            '& .MuiDataGrid-toolbarContainer': {
              padding: '2px 6px',
              borderBottom: '1px solid #e0e0e0',
              gap: '2px',
              minHeight: '30px',
            },
            '& .MuiButton-root': {
              padding: '2px 6px',
              fontSize: '11px',
              minHeight: '24px',
            },
            '& .MuiTextField-root': {
              '& .MuiInputBase-root': {
                fontSize: '11px',
                padding: '0px 2px',
                minHeight: '24px',
              },
              '& .MuiInputBase-input': {
                padding: '2px 6px',
              },
            },
            '& .MuiDataGrid-overlay': {
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
            },
            ...(isCardView && {
              '& .MuiDataGrid-virtualScroller': { display: 'none' },
              '& .MuiDataGrid-footerContainer': { display: 'none' },
              '& .MuiDataGrid-columnHeaders': { display: 'none' },
            }),
          }}
        />
      </DataGridContainer>
      {isCardView && (
        <CardViewContainer>
          {isGenerating ? (
            <LoadingContainer>
              <CircularProgress size={32} />
              <LoadingText>Generating messages…</LoadingText>
            </LoadingContainer>
          ) : (
            <>
              <CardGridLayout>
                {pagedCardRows.map((msg) => (
                  <MessageCard
                    key={msg.identifier}
                    message={msg}
                    hasRank={hasRank}
                    selectable={savingEnabled}
                    selected={(selectionModel as string[]).includes(msg.identifier)}
                    onSelect={handleCardSelect}
                    onViewEvidence={setEvidenceMessage}
                  />
                ))}
                {pagedCardRows.length === 0 && (
                  <EmptyCards>No messages match the current filters.</EmptyCards>
                )}
              </CardGridLayout>
              {totalCardPages > 1 && (
                <CardPagination>
                  <PaginationButton disabled={validCardPage === 0} onClick={() => setCardPage((p) => p - 1)}>
                    ← Previous
                  </PaginationButton>
                  <PaginationInfo>
                    Page {validCardPage + 1} of {totalCardPages} ({cardRows.length} messages)
                  </PaginationInfo>
                  <PaginationButton
                    disabled={validCardPage >= totalCardPages - 1}
                    onClick={() => setCardPage((p) => p + 1)}
                  >
                    Next →
                  </PaginationButton>
                </CardPagination>
              )}
            </>
          )}
        </CardViewContainer>
      )}
      <SupportingEvidenceModal
        open={evidenceMessage !== null}
        onClose={() => setEvidenceMessage(null)}
        message={evidenceMessage}
      />
    </Root>
  );
}

// ─── MessageCard Component ──────────────────────────────────────────────────

interface MessageCardProps {
  message: MarketingMessage;
  hasRank: boolean;
  selectable: boolean;
  onViewEvidence: (message: MarketingMessage) => void;
  selected: boolean;
  onSelect: (identifier: string) => void;
}

function MessageCard({
  message,
  hasRank,
  selectable,
  selected,
  onSelect,
  onViewEvidence,
}: MessageCardProps): React.JSX.Element {
  const need = message.doctorNeedAddressed;
  const hcpText = need ? `${need.themeLabel} / ${need.domainLabel} / ${need.frame}` : null;

  return (
    <Card $saved={message.saved} $selected={selected}>
      <CardHeader>
        <CardBadges>
          {hasRank && message.seqIdx != null && <RankBadge>#{message.seqIdx}</RankBadge>}
          <CategoryBadge>{normalizeCategory(message.category)}</CategoryBadge>
          <StatusBadge status={message.status}>{message.status}</StatusBadge>
          {message.confidence &&
            (message.confidenceBasis ? (
              <Tooltip title={message.confidenceBasis} enterDelay={500}>
                <ConfidenceBadge $level={getConfidenceLevel(message.confidence)}>
                  {formatConfidence(message.confidence)}
                </ConfidenceBadge>
              </Tooltip>
            ) : (
              <ConfidenceBadge $level={getConfidenceLevel(message.confidence)}>
                {formatConfidence(message.confidence)}
              </ConfidenceBadge>
            ))}
        </CardBadges>
        {selectable && (
          <SelectionIndicator>
            {message.saved ? (
              <Tooltip title="Already saved" enterDelay={300}>
                <SavedCheckmark>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </SavedCheckmark>
              </Tooltip>
            ) : (
              <CardCheckbox
                type="checkbox"
                checked={selected}
                onChange={() => onSelect(message.identifier)}
              />
            )}
          </SelectionIndicator>
        )}
      </CardHeader>
      <CardTitle>{message.headline}</CardTitle>
      <CardBody>{message.body}</CardBody>
      {hcpText && (
        <CardInfoRow>
          <CardInfoItem>
            <CardInfoLabel>HCP Matched Attributes</CardInfoLabel>
            <Tooltip title={hcpText} enterDelay={500}>
              <CardInfoValue $truncate>{hcpText}</CardInfoValue>
            </Tooltip>
          </CardInfoItem>
        </CardInfoRow>
      )}
      <CardActions>
        {message.metrics && <MetricsPopoverTrigger metrics={message.metrics} variant="button" />}
        <ViewEvidenceButton onClick={() => onViewEvidence(message)}>Evidence</ViewEvidenceButton>
      </CardActions>
    </Card>
  );
}

// ─── MetricsPopoverTrigger ───────────────────────────────────────────────────

interface MetricsPopoverTriggerProps {
  metrics: MessageMetrics;
  variant?: 'icon' | 'button';
}

function MetricsPopoverTrigger({ metrics, variant = 'icon' }: MetricsPopoverTriggerProps): React.JSX.Element {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  return (
    <>
      {variant === 'button' ? (
        <ViewMetricsButton onClick={(e) => setAnchorEl(e.currentTarget)}>View Metrics</ViewMetricsButton>
      ) : (
        <MetricsIconButton onClick={(e) => setAnchorEl(e.currentTarget)} title="View Metrics">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <rect x="1" y="9" width="3" height="6" rx="0.5" />
            <rect x="5.5" y="5" width="3" height="10" rx="0.5" />
            <rect x="10" y="1" width="3" height="14" rx="0.5" />
          </svg>
        </MetricsIconButton>
      )}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { borderRadius: '10px', boxShadow: '0 4px 20px rgba(0,0,0,0.12)' } } }}
      >
        <PopoverContent>
          <PopoverTitle>Metrics</PopoverTitle>
          <MetricsGrid>
            {METRIC_KEYS.map((key) => {
              const value = metrics[key];
              if (value == null) return null;
              const basisKey = `${key}Basis` as const;
              const basis = metrics[basisKey];
              const pct = Math.round(value * 100);
              return (
                <MetricItem key={key}>
                  <MetricLabelRow>
                    <MetricLabel>{camelToTitle(key)}</MetricLabel>
                    <MetricValue>{pct}%</MetricValue>
                  </MetricLabelRow>
                  {basis ? (
                    <Tooltip title={basis} enterDelay={500}>
                      <MetricBar>
                        <MetricFill style={{ width: `${pct}%` }} />
                      </MetricBar>
                    </Tooltip>
                  ) : (
                    <MetricBar>
                      <MetricFill style={{ width: `${pct}%` }} />
                    </MetricBar>
                  )}
                </MetricItem>
              );
            })}
          </MetricsGrid>
        </PopoverContent>
      </Popover>
    </>
  );
}

// ─── Styled Components ──────────────────────────────────────────────────────

const Root = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: white;
  border-radius: 12px;
  overflow: hidden;
`;

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  padding: 6px 8px;
  border-bottom: 1px solid #e0e0e0;
  background: #f8f9fa;
`;

const DataGridContainer = styled.div<{ $cardMode?: boolean }>`
  ${({ $cardMode }) => ($cardMode ? `flex: none; min-height: auto;` : `flex: 1; min-height: 0;`)}
  position: relative;
  width: 100%;
`;

const TitleCell = styled.div`
  font-weight: 500;
  color: #1a1a1a;
`;

const MessageCell = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #666;
`;

const TooltipCell = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: default;
`;

const CategoryBadge = styled.span`
  padding: 4px 10px;
  background: ${(props) => props.theme.palette.sentiment.primary.lighter};
  color: ${(props) => props.theme.palette.sentiment.primary.main};
  border-radius: 10px;
  font-size: 11px;
  font-weight: 500;
`;

const StatusBadge = styled.span<{ status: 'draft' | 'approved' | 'published' }>`
  padding: 4px 10px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 500;
  text-transform: capitalize;
  background: ${(props) => {
    switch (props.status) {
      case 'draft':
        return '#fef3c7';
      case 'approved':
        return '#d1fae5';
      case 'published':
        return '#dbeafe';
      default:
        return '#f5f5f5';
    }
  }};
  color: ${(props) => {
    switch (props.status) {
      case 'draft':
        return '#92400e';
      case 'approved':
        return '#065f46';
      case 'published':
        return '#1e40af';
      default:
        return '#666';
    }
  }};
`;

const TableCheckbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: #1271c4;
  margin: 0;
`;

const TableSavedCheckmark = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  cursor: default;

  svg {
    opacity: 0.7;
  }
`;

// ─── Card View Styled Components ────────────────────────────────────────────

const CardViewContainer = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 12px;
`;

const CardGridLayout = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
  gap: 12px;
`;

const Card = styled.div<{ $saved?: boolean; $selected?: boolean }>`
  border: 1px solid ${({ $saved, $selected }) => ($saved ? '#10b981' : $selected ? '#1271c4' : '#e0e0e0')};
  border-radius: 10px;
  padding: 14px;
  background: ${({ $saved }) => ($saved ? 'rgba(16, 185, 129, 0.05)' : 'white')};
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease;
  display: flex;
  flex-direction: column;
  gap: 10px;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const CardBadges = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
`;

const RankBadge = styled.span`
  padding: 2px 8px;
  background: #1271c4;
  color: white;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
`;

const CONFIDENCE_COLORS: Record<ConfidenceLevel, { bg: string; color: string }> = {
  high: { bg: '#d1fae5', color: '#065f46' },
  medium: { bg: '#fef3c7', color: '#92400e' },
  low: { bg: '#fee2e2', color: '#991b1b' },
};

const ConfidenceBadge = styled.span<{ $level: ConfidenceLevel }>`
  padding: 4px 10px;
  background: ${({ $level }) => CONFIDENCE_COLORS[$level].bg};
  color: ${({ $level }) => CONFIDENCE_COLORS[$level].color};
  border-radius: 10px;
  font-size: 11px;
  font-weight: 500;
  cursor: default;
  white-space: nowrap;
`;

const SelectionIndicator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
`;

const SavedCheckmark = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  color: #9ca3af;
  cursor: default;

  svg {
    opacity: 0.7;
  }
`;

const CardCheckbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: #1271c4;
  margin: 0;

  &:disabled {
    cursor: default;
    accent-color: #9ca3af;
    opacity: 0.6;
  }
`;

const CardTitle = styled.div`
  font-weight: 600;
  font-size: 14px;
  color: #1a1a1a;
  line-height: 1.3;
`;

const CardBody = styled.div`
  font-size: 12px;
  color: #555;
  line-height: 1.5;
`;

const CardInfoRow = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  padding-top: 4px;
  border-top: 1px solid #f0f0f0;
`;

const CardInfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
`;

const CardInfoLabel = styled.span`
  font-size: 10px;
  font-weight: 600;
  color: #999;
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

const CardInfoValue = styled.div<{ $truncate?: boolean }>`
  font-size: 12px;
  color: #424242;
  cursor: default;
  ${({ $truncate }) =>
    $truncate &&
    `overflow: hidden;
     text-overflow: ellipsis;
     white-space: nowrap;`}
`;

const MetricsIconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: 1px solid #d0d0d0;
  border-radius: 6px;
  background: white;
  color: #666;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: #f0f0f0;
    color: #1271c4;
  }
`;

const ViewMetricsButton = styled.button`
  padding: 4px 12px;
  border: 1px solid #d0d0d0;
  border-radius: 6px;
  background: white;
  color: #555;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: #f0f0f0;
    color: #1271c4;
    border-color: #1271c4;
  }
`;

const EvidenceIconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: 1px solid #d0d0d0;
  border-radius: 6px;
  background: white;
  color: #666;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: #f0f0f0;
    color: #1271c4;
  }
`;

const ViewEvidenceButton = styled.button`
  padding: 4px 12px;
  border: 1px solid #d0d0d0;
  border-radius: 6px;
  background: white;
  color: #555;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: #f0f0f0;
    color: #1271c4;
    border-color: #1271c4;
  }
`;

const CardActions = styled.div`
  display: flex;
  gap: 8px;
  padding-top: 4px;
  border-top: 1px solid #f0f0f0;
`;

const PopoverContent = styled.div`
  padding: 16px;
  width: 340px;
`;

const PopoverTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 12px;
`;

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 6px;
`;

const MetricItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const MetricLabel = styled.span`
  font-size: 10px;
  color: #888;
  font-weight: 500;
`;

const MetricBar = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  height: 14px;
  background: #f0f0f0;
  border-radius: 7px;
  overflow: hidden;
  position: relative;
  cursor: default;
`;

const MetricFill = styled.div`
  height: 100%;
  background: #1271c4;
  border-radius: 7px;
  min-width: 2px;
  transition: width 0.3s ease;
`;

const MetricLabelRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
`;

const MetricValue = styled.span`
  font-size: 10px;
  font-weight: 600;
  color: #555;
`;

const CardPagination = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 12px 0 4px;
`;

const PaginationButton = styled.button`
  padding: 4px 12px;
  border: 1px solid #d0d0d0;
  border-radius: 6px;
  background: white;
  color: #424242;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: #f0f0f0;
  }

  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
`;

const PaginationInfo = styled.span`
  font-size: 11px;
  color: #888;
`;

const EmptyCards = styled.div`
  grid-column: 1 / -1;
  text-align: center;
  padding: 40px 20px;
  color: #999;
  font-size: 13px;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-height: 200px;
`;

const LoadingText = styled.span`
  font-size: 13px;
  color: #888;
`;
